var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCaretDown, faCaretUp, faShieldAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useDebounceFn, useEventBus } from "@vueuse/core";
import { BAlert, BButton, BCard, BFormCheckbox, BOverlay, BPagination } from "bootstrap-vue";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router/composables";
import {} from "./configs/types";
import HelpText from "../Help/HelpText.vue";
import SwitchToHistoryLink from "../History/SwitchToHistoryLink.vue";
import GridBoolean from "./GridElements/GridBoolean.vue";
import GridDatasets from "./GridElements/GridDatasets.vue";
import GridExpand from "./GridElements/GridExpand.vue";
import GridLink from "./GridElements/GridLink.vue";
import GridOperations from "./GridElements/GridOperations.vue";
import GridText from "./GridElements/GridText.vue";
import FilterMenu from "@/components/Common/FilterMenu.vue";
import Heading from "@/components/Common/Heading.vue";
import SharingIndicators from "@/components/Indices/SharingIndicators.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
import StatelessTags from "@/components/TagsMultiselect/StatelessTags.vue";
import UtcDate from "@/components/UtcDate.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'GridList',
    props: {
        gridConfig: null,
        gridMessage: { default: "" },
        noDataMessage: { default: "No data available." },
        delay: { default: 5000 },
        embedded: { type: Boolean, default: false },
        limit: { default: 25 },
        usernameSearch: { default: "" },
        extraProps: { default: undefined }
    },
    setup(__props) {
        const props = __props;
        const eventBus = useEventBus("grid-router-push");
        const router = useRouter();
        library.add(faCaretDown, faCaretUp, faShieldAlt);
        // contains the current grid data provided by the corresponding api endpoint
        const gridData = ref();
        // message references
        const errorMessage = ref("");
        const operationMessage = ref("");
        const operationStatus = ref("");
        // selection references
        const selected = ref(new Set());
        const selectedAll = computed(() => gridData.value.length === selected.value.size);
        const selectedIndeterminate = computed(() => ![0, gridData.value.length].includes(selected.value.size));
        // expand references
        const expanded = ref(new Set());
        // page references
        const currentPage = ref(1);
        const totalRows = ref(0);
        // loading indicators
        const initDataLoading = ref(true);
        const resultsLoading = ref(false);
        // check if `initDataLoading` has completed and data rows are available
        const isAvailable = computed(() => !initDataLoading.value && totalRows.value > 0);
        // sort references
        const sortBy = ref(props.gridConfig ? props.gridConfig.sortBy : "");
        const sortDesc = ref(props.gridConfig ? props.gridConfig.sortDesc : false);
        // filtering refs and handlers
        const filterText = ref("");
        const showAdvanced = ref(false);
        const filterClass = props.gridConfig.filtering;
        const rawFilters = computed(() => Object.fromEntries((filterClass === null || filterClass === void 0 ? void 0 : filterClass.getFiltersForText(filterText.value, true, false)) || []));
        const validFilters = computed(() => filterClass === null || filterClass === void 0 ? void 0 : filterClass.getValidFilters(rawFilters.value, true).validFilters);
        const invalidFilters = computed(() => filterClass === null || filterClass === void 0 ? void 0 : filterClass.getValidFilters(rawFilters.value, true).invalidFilters);
        const isSurroundedByQuotes = computed(() => /^["'].*["']$/.test(filterText.value));
        const hasInvalidFilters = computed(() => !isSurroundedByQuotes.value && Object.keys(invalidFilters.value || {}).length > 0);
        // hide message helper
        const hideMessage = useDebounceFn(() => {
            operationMessage.value = "";
        }, props.delay);
        /**
         * Manually set filter value, used for tags and `SharingIndicators`
         */
        function applyFilter(filter, value, quoted = false) {
            var _a;
            const filtering = (_a = props.gridConfig) === null || _a === void 0 ? void 0 : _a.filtering;
            const quotedValue = quoted ? `'${value}'` : value;
            if (filtering) {
                filterText.value = filtering.setFilterValue(filterText.value, filter, quotedValue.toString()) || "";
            }
        }
        /**
         * Display initial message parsed through route query
         */
        function displayInitialMessage() {
            if (props.gridMessage) {
                operationMessage.value = props.gridMessage;
                operationStatus.value = "success";
            }
        }
        /**
         * Returns the appropriate text for a field entry
         */
        function fieldText(fieldEntry, rowData) {
            if (fieldEntry.converter) {
                return fieldEntry.converter(rowData);
            }
            else {
                return rowData[fieldEntry.key];
            }
        }
        /**
         * Returns the appropriate column header title for a field entry
         */
        function fieldTitle(fieldEntry) {
            if (fieldEntry.title) {
                return fieldEntry.title;
            }
            else if (fieldEntry.title === undefined || fieldEntry.title === "") {
                return fieldEntry.key.charAt(0).toUpperCase() + fieldEntry.key.slice(1).replace(/_/g, " ").toLowerCase();
            }
            else {
                return null;
            }
        }
        /**
         * Request grid data
         */
        function getGridData() {
            return __awaiter(this, void 0, void 0, function* () {
                resultsLoading.value = true;
                selected.value = new Set();
                if (props.gridConfig) {
                    if (hasInvalidFilters.value) {
                        // there are invalid filters, so we don't want to search
                        initDataLoading.value = false;
                        resultsLoading.value = false;
                        return;
                    }
                    try {
                        const offset = props.limit * (currentPage.value - 1);
                        const [responseData, responseTotal] = yield props.gridConfig.getData(offset, props.limit, validatedFilterText(), sortBy.value, sortDesc.value, props.extraProps);
                        gridData.value = responseData;
                        totalRows.value = responseTotal;
                        errorMessage.value = "";
                    }
                    catch (e) {
                        errorMessage.value = `Failed to obtain grid data: ${e}`;
                    }
                    finally {
                        initDataLoading.value = false;
                        resultsLoading.value = false;
                    }
                }
            });
        }
        /**
         * Execute grid operation and display message if available
         */
        function onBatchOperation(operation, rowDataArray) {
            return __awaiter(this, void 0, void 0, function* () {
                const response = yield operation.handler(rowDataArray);
                if (response) {
                    yield getGridData();
                    operationMessage.value = response.message;
                    operationStatus.value = response.status || "success";
                }
            });
        }
        function onOperation(operation, rowData) {
            return __awaiter(this, void 0, void 0, function* () {
                const response = yield operation.handler(rowData);
                if (response) {
                    yield getGridData();
                    operationMessage.value = response.message;
                    operationStatus.value = response.status || "success";
                }
            });
        }
        /**
         * Handle router push request emitted by grid module
         */
        function onRouterPush(route, options = {}) {
            // reset expanded rows before navigating
            expanded.value = new Set();
            // @ts-ignore
            nextTick(() => {
                // @ts-ignore
                router.push(route, options);
            });
        }
        /**
         * User changes sorting
         */
        function onSort(sortKey) {
            if (sortBy.value !== sortKey) {
                sortBy.value = sortKey;
                sortDesc.value = false;
            }
            else {
                sortDesc.value = !sortDesc.value;
            }
        }
        /**
         * Process tag inputs
         */
        function onTagInput(data, tags, tagsHandler) {
            if (tagsHandler) {
                tagsHandler(Object.assign(Object.assign({}, data), { tags: tags }));
                data.tags = tags;
            }
        }
        function onFilter(filter) {
            if (filter) {
                applyFilter(filter, true);
            }
        }
        // Select multiple rows
        function onSelect(rowData) {
            if (selected.value.has(rowData)) {
                selected.value.delete(rowData);
            }
            else {
                selected.value.add(rowData);
            }
            selected.value = new Set(selected.value);
        }
        function onSelectAll(current) {
            if (current) {
                selected.value = new Set(gridData.value);
            }
            else {
                selected.value = new Set();
            }
        }
        /**
         * Show details for a row
         */
        function showDetails(rowData, show) {
            if (show) {
                expanded.value.add(rowData);
            }
            else {
                expanded.value.delete(rowData);
            }
            expanded.value = new Set(expanded.value);
        }
        /**
         * A valid filter/query for the backend
         */
        function validatedFilterText() {
            if (isSurroundedByQuotes.value) {
                // the filterText is surrounded by quotes, remove them
                return filterText.value.slice(1, -1);
            }
            else if (Object.keys(rawFilters.value).length === 0) {
                // there are no filters derived from the `filterText`
                return filterText.value;
            }
            // there are valid filters derived from the `filterText`
            return (filterClass === null || filterClass === void 0 ? void 0 : filterClass.getFilterText(validFilters.value || {}, false)) || "";
        }
        /**
         * Initialize grid data
         */
        onMounted(() => {
            if (props.usernameSearch) {
                filterText.value = (filterClass === null || filterClass === void 0 ? void 0 : filterClass.setFilterValue(filterText.value, "user", `'${props.usernameSearch}'`)) || "";
            }
            getGridData();
            eventBus.on(onRouterPush);
            displayInitialMessage();
        });
        onUnmounted(() => {
            eventBus.off(onRouterPush);
        });
        /**
         * Load current page
         */
        watch([currentPage, filterText, sortDesc, sortBy], () => getGridData());
        /**
         * Operation message timeout handler
         */
        watch(operationMessage, () => {
            hideMessage();
        });
        return { __sfc: true, eventBus, router, props, gridData, errorMessage, operationMessage, operationStatus, selected, selectedAll, selectedIndeterminate, expanded, currentPage, totalRows, initDataLoading, resultsLoading, isAvailable, sortBy, sortDesc, filterText, showAdvanced, filterClass, rawFilters, validFilters, invalidFilters, isSurroundedByQuotes, hasInvalidFilters, hideMessage, applyFilter, displayInitialMessage, fieldText, fieldTitle, getGridData, onBatchOperation, onOperation, onRouterPush, onSort, onTagInput, onFilter, onSelect, onSelectAll, showDetails, validatedFilterText, FontAwesomeIcon, BAlert, BButton, BCard, BFormCheckbox, BOverlay, BPagination, HelpText, SwitchToHistoryLink, GridBoolean, GridDatasets, GridExpand, GridLink, GridOperations, GridText, FilterMenu, Heading, SharingIndicators, LoadingSpan, StatelessTags, UtcDate };
    }
});
